import { create } from 'zustand';
import api from '../utils/api';

/**
 * Espace client multi-comptes : un même compte peut accéder à plusieurs clients (ex : Démé +
 * Démé Chez Toi) et switcher de profil via l'icône du header. Le client actif est stocké en
 * localStorage (`nudge-active-client`) et envoyé en en-tête `x-active-client` (cf api.ts).
 */
export interface ClientProfile {
  id: string;
  name: string;
  profilePicUrl: string | null;
  isPrimary?: boolean;
}

const LS_KEY = 'nudge-active-client';

function readStored(): string | null {
  try {
    return localStorage.getItem(LS_KEY);
  } catch {
    return null;
  }
}
function writeStored(id: string | null): void {
  try {
    if (id) localStorage.setItem(LS_KEY, id);
    else localStorage.removeItem(LS_KEY);
  } catch {
    /* ignore */
  }
}

interface ClientProfileState {
  profiles: ClientProfile[];
  activeClientId: string | null;
  loaded: boolean;
  /** Charge la liste des profils accessibles + détermine le client actif. */
  fetchProfiles: () => Promise<void>;
  /** Change de profil actif (persisté + en-tête). Le refetch des données est déclenché côté page. */
  setActive: (clientId: string) => void;
  /** Reset (déconnexion / fin d'impersonation). */
  reset: () => void;
}

export const useClientProfileStore = create<ClientProfileState>((set, get) => ({
  profiles: [],
  activeClientId: readStored(),
  loaded: false,

  fetchProfiles: async () => {
    try {
      const res = await api.get('/client-portal/profiles');
      const profiles: ClientProfile[] = res.data?.profiles || [];
      const serverActive: string | null = res.data?.activeClientId || null;
      // On garde le choix local s'il est toujours accessible, sinon on suit le serveur.
      const stored = get().activeClientId;
      const active = stored && profiles.some(p => p.id === stored) ? stored : serverActive;
      writeStored(active);
      set({ profiles, activeClientId: active, loaded: true });
    } catch {
      set({ loaded: true });
    }
  },

  setActive: (clientId: string) => {
    if (clientId === get().activeClientId) return;
    writeStored(clientId);
    set({ activeClientId: clientId });
  },

  reset: () => {
    writeStored(null);
    set({ profiles: [], activeClientId: null, loaded: false });
  },
}));
